import React, { useEffect, useState } from 'react';
import Seo from '../../seo/Seo';
import { ConsentGate, FormConsent } from '../../privacy/ConsentUI';
import { RETENTION } from '../../privacy/privacyConfig';
import ReCAPTCHA from 'react-google-recaptcha';
import './Register.css';
import {
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import countries from 'i18n-iso-countries';
import english from 'i18n-iso-countries/langs/en.json';
import { API_ACCOUNT_REGISTER_ENDPOINT, API_AUTH_ENDPOINT, API_BASE_URL, API_VERIFY_MFA_ENDPOINT, BI_APP_URI, DASHBOARD_APP_URI, DOMAIN, SIGNUP_INDUSTRIES } from '../../constants/constants';
import { COUNTRY_CODES } from '../../constants/countries';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

export const Registration = () => {
  const RegisterEndpoint = API_BASE_URL + API_ACCOUNT_REGISTER_ENDPOINT;
  const VerifyMfaEndpoint = API_BASE_URL + API_AUTH_ENDPOINT + API_VERIFY_MFA_ENDPOINT;
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [location, setLocation] = useState('');
  const [industry, setIndustry] = useState('');
  const [userName, setUserName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [password, setPassword] = useState('');
  const [signInEmail, setSignInEmail] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const companySizes = ['1-50', '50-200', '200+'];
  const allCountries = Object.values(countries.getNames('en'));
  const [phoneError, setPhoneError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [signInEmailError, setSignInEmailError] = useState(false);
  const [isLowercase, setIsLowercase] = useState(false);
  const [isUppercase, setIsUppercase] = useState(false);
  const [isDigit, setIsDigit] = useState(false);
  const [isSpecialChar, setIsSpecialChar] = useState(false);
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [agreed, setAgreed] = useState(false); // DPDP consent — never pre-ticked
  const [consentError, setConsentError] = useState(false);

  countries.registerLocale(english);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\d{10}$/;
  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,15}$/;

  useEffect(() => {
    setIsLowercase(/[a-z]/.test(password));
    setIsUppercase(/[A-Z]/.test(password));
    setIsDigit(/\d/.test(password));
    setIsSpecialChar(/[^\da-zA-Z]/.test(password));
    setIsLengthValid(password.length >= 8 && password.length <= 15);
  }, [password]);

  // Check if email already exists when user types in the email field
  useEffect(() => {
    const trimmed = signInEmail.trim();
    if (!trimmed || !emailRegex.test(trimmed)) {
      setEmailExists(false);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await fetch(`${API_BASE_URL}/RayaAccountManagement/check-email?email=${encodeURIComponent(trimmed)}`);
        if (res.ok) {
          const data = await res.json();
          setEmailExists(data.exists);
          if (data.exists) setPassword('');
        }
      } catch { setEmailExists(false); }
      finally { setCheckingEmail(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [signInEmail]);

  const regTabs = [
    { label: 'About You', value: 1 },
    { label: 'Create Account', value: 2 },
    { label: 'Scan QR Code', value: 3 },
  ];

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    setPhoneError(!phoneRegex.test(e.target.value));
  };

  const handleSignInEmailChange = (e) => {
    setSignInEmail(e.target.value);
    setSignInEmailError(!emailRegex.test(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setConsentError(true);
      return;
    }

    if (!captchaVerified) {
      setErrorMessage('Please verify the CAPTCHA');
      return;
    }

    if (!phoneRegex.test(phone)) {
      setErrorMessage('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const formData = {
      CompanyName: companyName,
      CompanySize: parseInt(companySize),
      Country: location,
      Industry: industry,
      AdminEmailAddress: signInEmail,
      JobTitle: jobTitle,
      PhoneNumber: phone,
      CountryCode: countryCode,
      FullName: userName,
      AdminSignInEmailAddress: signInEmail,
      AdminPassword: password,
      license: 0,
    };

    const jsonData = JSON.stringify(formData);

    try {
      const response = await fetch(RegisterEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: jsonData,
      });

      if (!response.ok) {
        if (response.status === 400) {
          const errorText = await response.text();
          setErrorMessage(errorText);
        } else {
          setErrorMessage('An error occurred while registering. Please try again later.');
        }
      } else {
        const responseData = await response.json();
        if (responseData.existingUser) {
          // Existing user registering a new tenant — skip MFA setup, go to login
          // They'll see the new tenant in the tenant picker
          navigate('/login', { state: { message: 'Your new organization has been created! Sign in to access it.' } });
        } else if (responseData.qrCodeImageAsBase64) {
          setQrCodeImage(responseData.qrCodeImageAsBase64);
          setStep(3);
        } else {
          setErrorMessage('Registration completed but an unexpected response was received.');
        }
      }
    } catch (error) {
      console.error('An error occurred while registering:', error);
      setErrorMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(true);
  };

  const decodeJWT = (token) => {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const handleVerifyCode = async (code) => {
    setIsVerifying(true);
    setVerifyError('');
    try {
      const response = await fetch(VerifyMfaEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddress: signInEmail,
          totpCode: code,
        }),
      });

      if (response.ok) {
        const { token } = await response.json();
        Cookies.set('authToken', token, { domain: DOMAIN, secure: true, sameSite: 'None', path: '/' });
        setVerifySuccess(true);
        const decodedToken = decodeJWT(token);
        // Brief delay so user sees success state before redirect
        setTimeout(() => {
          if (decodedToken?.RayaLicense === 'BIAndBS') {
            window.location.href = `${DASHBOARD_APP_URI}dashboard?token=${token}`;
          } else if (decodedToken?.RayaLicense === 'BI') {
            window.location.href = `${BI_APP_URI}dashboard?token=${token}`;
          } else if (decodedToken?.RayaLicense === 'BS') {
            window.location.href = `${DASHBOARD_APP_URI}dashboard?token=${token}`;
          }
        }, 1200);
      } else {
        setVerifyError('Invalid code. Please check your authenticator app and try again.');
        setVerificationCode('');
      }
    } catch (error) {
      console.error('An error occurred while verifying the code:', error);
      setVerifyError('An error occurred. Please try again later.');
      setVerificationCode('');
    } finally {
      if (!verifySuccess) setIsVerifying(false);
    }
  };

  const PasswordRequirement = ({ met, label }) => (
    <div className={`password-requirement ${met ? 'met' : ''}`}>
      {met ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
      <span>{label}</span>
    </div>
  );

  return (
    <Container maxWidth="lg" className="registration-container">
      <Seo path="/register" />
      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          {/* Stepper */}
          <Box className="registration-progress-container">
            {regTabs.map((item, idx) => (
              <div className="registration-progress-step" key={item.value}>
                {idx > 0 && <div className={`registration-progress-line ${step > idx ? 'active' : ''}`} />}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box className={`registration-progress-bubble ${step === item.value ? 'active' : step > item.value ? 'completed' : ''}`}>
                    {step > item.value ? '✔' : item.value}
                  </Box>
                  <span className={`registration-progress-label ${step === item.value ? 'active' : step > item.value ? 'completed' : ''}`}>
                    {item.label}
                  </span>
                </div>
              </div>
            ))}
          </Box>

          {/* Form Card */}
          <div className="registration-step-card">
            <form onSubmit={handleSubmit}>
              {/* Step 1: About You */}
              {step === 1 && (
                <>
                  <h1 className="registration-step-title">Tell us about you</h1>
                  <div className="registration-step-subtitle">We'll personalize your experience based on this.</div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Company Name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        autoFocus
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Full Name" value={userName} onChange={(e) => setUserName(e.target.value)} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Job Title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} required fullWidth size="small" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Autocomplete
                        size="small"
                        options={COUNTRY_CODES}
                        getOptionLabel={(o) => `${o.flag} ${o.label} (${o.code})`}
                        value={COUNTRY_CODES.find(c => c.code === countryCode) || null}
                        onChange={(_, v) => setCountryCode(v?.code || "")}
                        sx={{ '& .MuiAutocomplete-input': { fontSize: 15, letterSpacing: 0 } }}
                        renderInput={(params) => <TextField {...params} label="Code *" variant="outlined" />}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ fontSize: 13 }}>
                            <Box component="span" sx={{ mr: 1, fontSize: 16 }}>{option.flag}</Box>
                            {option.label} ({option.code})
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Phone Number"
                        type="tel"
                        value={phone}
                        onChange={handlePhoneChange}
                        error={phoneError && phone.length > 0}
                        helperText={phoneError && phone.length > 0 ? 'Invalid' : ''}
                        required
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Location</InputLabel>
                        <Select value={location} label="Location" onChange={(e) => setLocation(e.target.value)}>
                          {allCountries.map((country, index) => (
                            <MenuItem key={index} value={country}>{country}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Industry</InputLabel>
                        <Select value={industry} label="Industry" onChange={(e) => setIndustry(e.target.value)}>
                          {SIGNUP_INDUSTRIES.map((ind) => (
                            <MenuItem key={ind} value={ind}>{ind}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" fullWidth>
                        <Typography sx={{ fontSize: '0.85rem', color: '#475569', mb: 0.5 }}>Number of employees</Typography>
                        <RadioGroup row name="companySize" value={companySize} onChange={(e) => setCompanySize(e.target.value)}>
                          {companySizes.map((size, index) => (
                            <FormControlLabel key={index} value={index} control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.85rem' }}>{size}</Typography>} />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={nextStep} disabled={!companyName || !userName || !jobTitle || !phone || phoneError || !countryCode || !companySize || !location || !industry}
                      sx={{ textTransform: 'none', borderRadius: 2, px: 4, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
                      Continue
                    </Button>
                  </Box>
                </>
              )}

              {/* Step 2: Create Account */}
              {step === 2 && (
                <>
                  <h1 className="registration-step-title">Create your account</h1>
                  <div className="registration-step-subtitle">Set up your login credentials.</div>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Sign in Email"
                        type="email"
                        value={signInEmail}
                        onChange={handleSignInEmailChange}
                        error={signInEmailError && signInEmail.length > 0}
                        helperText={
                          checkingEmail ? 'Checking email...' :
                          emailExists ? 'This email is already registered — you\'ll use your existing password to sign in.' :
                          signInEmailError && signInEmail.length > 0 ? 'Enter a valid email address' : ''
                        }
                        required
                        fullWidth
                        size="small"
                        autoFocus
                        FormHelperTextProps={{ sx: { color: emailExists ? '#059669' : undefined } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={emailExists ? 'Existing Password' : 'Password'}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        helperText={emailExists ? 'Enter your existing account password to verify ownership.' : ''}
                        FormHelperTextProps={{ sx: { color: '#64748b' } }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                                {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {!emailExists && (
                      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
                        <PasswordRequirement met={isLowercase} label="Lowercase letter" />
                        <PasswordRequirement met={isUppercase} label="Uppercase letter" />
                        <PasswordRequirement met={isDigit} label="Number" />
                        <PasswordRequirement met={isSpecialChar} label="Special character" />
                        <PasswordRequirement met={isLengthValid} label="8-15 characters" />
                      </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <ConsentGate
                        category="security"
                        title="Security check (Google reCAPTCHA)"
                        what="To stop automated sign-ups this form uses Google reCAPTCHA. Loading it sends your IP address, browser details and how you use this page to Google, which may set a cookie. Nothing is loaded until you allow it."
                      >
                        <ReCAPTCHA sitekey="6LfmbRkTAAAAAFe9WOdrSVqwWyZdqqjQ7CpVK5J3" onChange={handleCaptchaChange} />
                      </ConsentGate>
                    </Grid>
                  </Grid>
                  {errorMessage && (
                    <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, p: 1.5, bgcolor: '#fef2f2', borderRadius: 1 }}>
                      {errorMessage}
                    </Typography>
                  )}
                  <FormConsent
                    id="register-consent"
                    checked={agreed}
                    onChange={(v) => { setAgreed(v); if (v) setConsentError(false); }}
                    error={consentError}
                    purpose="We use your details to create and secure your Raya ERP account, set it up for your industry, and contact you about your trial."
                    data="your name, job title, work email, phone, company name, size, country and industry"
                    retention={RETENTION.signup}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 1, lineHeight: 1.4 }}>
                    By clicking Register, you also agree to the <a href="/privacy-policy#terms">Terms of Use</a> and the free trial terms.
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Button variant="text" onClick={prevStep} sx={{ textTransform: 'none', color: '#64748b' }}>Back</Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting || !agreed || !captchaVerified || !signInEmail || !password || (!emailExists && (!isLowercase || !isUppercase || !isDigit || !isSpecialChar || !isLengthValid))}
                      sx={{ textTransform: 'none', borderRadius: 2, px: 4, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                    >
                      {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Register & Start Free Trial'}
                    </Button>
                  </Box>
                </>
              )}

              {/* Step 3: QR Code */}
              {step === 3 && (
                <>
                  {verifySuccess ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircle sx={{ fontSize: 56, color: '#22c55e', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                        Account verified successfully!
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#64748b', mb: 2 }}>
                        Redirecting you to your dashboard...
                      </Typography>
                      <CircularProgress size={24} sx={{ color: '#2563eb' }} />
                    </Box>
                  ) : (
                    <>
                      <h1 className="registration-step-title">Set up two-factor authentication</h1>
                      <div className="registration-step-subtitle">
                        This adds an extra layer of security to your account. Follow these steps:
                      </div>
                      <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#334155', mb: 0.5 }}>
                          <strong>1.</strong> Download an authenticator app on your phone — e.g.{' '}
                          <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>, or <strong>Authy</strong>.
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#334155', mb: 0.5 }}>
                          <strong>2.</strong> Open the app and tap <strong>"+"</strong> or <strong>"Add account"</strong>, then choose <strong>"Scan QR code"</strong>.
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#334155' }}>
                          <strong>3.</strong> Point your camera at the QR code below. A 6-digit code will appear in the app — enter it here.
                        </Typography>
                      </Box>
                      <Box className="qr-code-container" display="flex" justifyContent="center" my={2}>
                        {qrCodeImage ? <img src={`data:image/png;base64,${qrCodeImage}`} alt="TOTP QR Code" /> : <CircularProgress />}
                      </Box>
                      <Typography sx={{ textAlign: 'center', fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
                        Enter the 6-digit code from your authenticator app
                      </Typography>
                      <Box sx={{ maxWidth: 280, mx: 'auto' }}>
                        <TextField
                          label="Verification Code"
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setVerificationCode(value);
                            if (value.length === 6) {
                              handleVerifyCode(value);
                            }
                          }}
                          inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '1.3rem' } }}
                          required
                          fullWidth
                          size="small"
                          autoFocus
                          disabled={isVerifying}
                        />
                      </Box>
                      {isVerifying && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <CircularProgress size={20} sx={{ color: '#2563eb', mr: 1, verticalAlign: 'middle' }} />
                          <Typography component="span" sx={{ fontSize: '0.85rem', color: '#2563eb' }}>
                            Verifying...
                          </Typography>
                        </Box>
                      )}
                      {verifyError && (
                        <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1.5, textAlign: 'center', p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                          {verifyError}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </form>
          </div>
        </Grid>

        {/* Right sidebar */}
        <Grid item xs={12} md={4}>
          <Box className="registration-right">
            <svg viewBox="0 0 280 180" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', marginBottom: 20, borderRadius: 12 }}>
              <rect width="280" height="180" rx="12" fill="#f8fafc" />
              <rect x="20" y="20" width="110" height="70" rx="8" fill="#fff" stroke="#e2e8f0" />
              <text x="32" y="42" fontSize="9" fill="#64748b">Inventory</text>
              <rect x="32" y="50" width="50" height="6" rx="3" fill="#2563eb" />
              <rect x="32" y="62" width="80" height="6" rx="3" fill="#bfdbfe" />
              <rect x="32" y="74" width="65" height="6" rx="3" fill="#dbeafe" />
              <rect x="150" y="20" width="110" height="70" rx="8" fill="#fff" stroke="#e2e8f0" />
              <text x="162" y="42" fontSize="9" fill="#64748b">Sales</text>
              <circle cx="195" cy="65" r="18" fill="none" stroke="#22c55e" strokeWidth="4" strokeDasharray="50 113" />
              <circle cx="195" cy="65" r="18" fill="none" stroke="#2563eb" strokeWidth="4" strokeDasharray="35 113" strokeDashoffset="-50" />
              <circle cx="195" cy="65" r="18" fill="none" stroke="#e2e8f0" strokeWidth="4" strokeDasharray="28 113" strokeDashoffset="-85" />
              <rect x="20" y="100" width="240" height="60" rx="8" fill="#fff" stroke="#e2e8f0" />
              <text x="32" y="120" fontSize="9" fill="#64748b">Recent Orders</text>
              <rect x="32" y="130" width="100" height="5" rx="2" fill="#e2e8f0" />
              <rect x="152" y="130" width="40" height="5" rx="2" fill="#dcfce7" />
              <rect x="32" y="142" width="80" height="5" rx="2" fill="#e2e8f0" />
              <rect x="152" y="142" width="40" height="5" rx="2" fill="#fef3c7" />
            </svg>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, fontSize: '1.25rem' }}>14-Day Free Trial</Typography>
            <Typography sx={{ color: '#64748b', mb: 2, fontSize: '0.85rem' }}>No credit card required. Cancel anytime.</Typography>
            <List disablePadding>
              {['Full access to all ERP & CRM modules', 'Works on any device, from anywhere', 'Role-based access for your team', 'Industry-specific setup out of the box', 'Free support during your trial'].map((text, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.4 }}>
                  <ListItemText primary={`✓ ${text}`} primaryTypographyProps={{ fontSize: '0.85rem', color: '#475569' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export const EmailVerification = () => {
  return (
    <Box className="email-verification">
      <Typography variant="h4">Check Your Email</Typography>
      <Typography>Please check your email to set up your login password and sign in to your account.</Typography>
    </Box>
  );
};
